import { useCallback, useRef } from 'react'
import { useProductFetcher } from '../../../api/useProductFetcher/useProductFetcher'
import { useWorkspace2Store } from '../../../Workspace2Store'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import type {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from '../../../types/types'

/**
 * Custom hook for syncing Article states from backend during PT activation.
 *
 * This hook handles the synchronization of unexpanded Articles that get activated
 * in the backend during PT activation but remain deactivated in the frontend
 * because their data hasn't been loaded yet.
 *
 * How it works:
 * 1. Fetches ALL Articles from backend using base call (via useProductFetcher)
 * 2. Compares backend Article state with frontend state
 * 3. Adds Articles with changed isHidden state to the activation list
 * 4. Backend state is treated as source of truth
 *
 * @returns Object with syncArticlesToActivationList function
 */
export const useArticleSync = () => {
    // Ref to store fetched backend Articles
    const backendArticlesRef = useRef<Partial<ContentItemViewNodesType>[]>([])

    // Use the callback pattern like usePlainTextContentMutation
    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            // Extract contentItemViewNodes from response
            const allBackendArticles =
                (response as GetProjectProductFamilyViewResponse)
                    .getProjectProductFamilyView?.contentItemViewNodes ||
                (response as GetProjectNuggetContentViewResponse)
                    .getProjectNugget?.contentItemViewNodes ||
                []

            // Store in ref so syncArticlesToActivationList can access it
            backendArticlesRef.current = allBackendArticles
        },
    })

    /**
     * Syncs Articles from backend to activation list
     * Fetches all Articles, compares with frontend, adds changed ones to activation list
     *
     * @param allItemsToActivate - Map to add synced Articles to
     */
    const syncArticlesToActivationList = useCallback(
        async (
            allItemsToActivate: Map<string, Partial<ContentItemViewNodesType>>
        ) => {
            try {
                // 1. Get current frontend state
                const storeState = useWorkspace2Store.getState()
                const frontendNodes = storeState.contentItemViewNodes

                // 2. Fetch all Articles from backend
                // Use empty array [] to fetch all Articles (base call)
                // Callback will store the results in backendArticlesRef
                await getContentItemViewNodesWithApolloClient([])

                const allBackendArticles = backendArticlesRef.current

                // 3. Find and add Articles that need syncing
                allBackendArticles.forEach(
                    (backendArticle: Partial<ContentItemViewNodesType>) => {
                        const frontendArticle = frontendNodes.find(
                            (n) => n.id === backendArticle.id
                        )

                        // Sync if Article is missing OR if isHidden state is different
                        if (
                            !frontendArticle ||
                            frontendArticle.isHidden !== backendArticle.isHidden
                        ) {
                            // 4. Add to activation list
                            // Backend state is source of truth - use it as-is
                            if (backendArticle.id) {
                                allItemsToActivate.set(
                                    backendArticle.id,
                                    backendArticle
                                )
                            }
                        }
                    }
                )

                // Clear ref after use
                backendArticlesRef.current = []
            } catch (error) {
                console.error(
                    '[useArticleSync] Failed to fetch Articles from backend:',
                    error
                )
                // Don't throw - just skip syncing on error
                // PT activation can continue without Article sync
            }
        },
        [getContentItemViewNodesWithApolloClient]
    )

    return { syncArticlesToActivationList }
}
